// @contexts/Document.context.tsx

import React, { createContext, useContext, ReactNode, useCallback } from 'react';
import * as Option from "@utils/Option";
import type { Section, SectionID, UserDocument } from "@ps-bridge/models/core/dataContracts";
import type { IOts, OptionTs } from "@ps-bridge/interop";
import type { OptionStateHandle } from "@ps-bridge/capabilitiesTypes";
import { useIoOptionState } from '@utils/useIoOptionState';
import { ioEffect } from '@utils/effects/branded';
import { requireContext } from '@contexts/requireContext';

import { ok, type Ok } from "@ps-bridge/ok";
import * as PSDocumentMutations from "@ps-bridge/models/documents/documentMutations";

type DocumentContextType = {
    document: OptionStateHandle<UserDocument>;
    activeSection: OptionStateHandle<SectionID>;
    controller: DocumentController;
};

type DocumentController = {
    updateSection: (updatedSection: Section) => IOts<Ok>;
    appendSection: (newSection: Section) => IOts<Ok>;
};

const DocumentContext = createContext<OptionTs<DocumentContextType>>(Option.none());

const updateSectionMutation =
    PSDocumentMutations.updateSection as (document: UserDocument) => (updated: Section) => UserDocument;
const appendSectionMutation =
    PSDocumentMutations.appendSection as (document: UserDocument) => (newSection: Section) => UserDocument;

type DocumentProviderProps = {
    children: ReactNode;
};

export const DocumentProvider: React.FC<DocumentProviderProps> = ({ children }) => {
    const documentHandle = useIoOptionState<UserDocument>(Option.none<UserDocument>());
    const activeSectionHandle = useIoOptionState<SectionID>(Option.none<SectionID>());
    const { read: document, setMaybe: setDocumentMaybe } = documentHandle;
    const documentValue = document as OptionTs<UserDocument>;

    const updateSection = useCallback(
        (updatedSection: Section): IOts<Ok> => {
            return Option.fold(documentValue, () => ioEffect(() => ok),  (value: UserDocument) =>
                setDocumentMaybe(
                    Option.some(
                        updateSectionMutation(value)(updatedSection),
                    ),
                ),);
        },
        [documentValue, setDocumentMaybe]
    );

    const appendSection = useCallback(
        (newSection: Section): IOts<Ok> => {
            return Option.fold(documentValue, () => ioEffect(() => ok),  (value: UserDocument) =>
                setDocumentMaybe(
                    Option.some(
                        appendSectionMutation(value)(newSection),
                    ),
                ),);
        },
        [documentValue, setDocumentMaybe]
    );

    const contextValue = Option.some({
        document: documentHandle,
        activeSection: activeSectionHandle,
        controller: {
            updateSection,
            appendSection,
        },
    });

    return <DocumentContext.Provider value={contextValue}>{children}</DocumentContext.Provider>;
};

export const useDocumentContext = (): DocumentContextType => {
    return requireContext(
        useContext(DocumentContext),
        'useDocumentContext must be used within a DocumentProvider',
    );
};

// This hook guarantees that the document is non-null
